import { useEffect, useRef, useState } from 'react';
import { Control, FieldValues, Path, PathValue, useController } from 'react-hook-form';

import { Editor, Element, Transforms } from 'slate';

import { useMounted } from '../../../../../../../utils/hooks';

export interface UseQuestionOption<T extends FieldValues> {
  control: Control<T, object>;
  name: Path<T>;
  initialValue: PathValue<T, Path<T>>;
}

const useQuestionOption = <T extends FieldValues>({ control, name, initialValue }: UseQuestionOption<T>) => {
  const editorRef = useRef<Editor | null>(null);

  const getIsMounted = useMounted();
  const [initialRichText, setInitialRichText] = useState(initialValue);

  useEffect(
    function initializeRichTextValue() {
      const isMounted = getIsMounted();
      if (!isMounted) return;
      setInitialRichText(initialValue);
    },
    [getIsMounted, initialValue]
  );

  const controllerMethods = useController({ control, name, defaultValue: initialRichText });
  const {
    field: { value: richText, onChange: onChangeRichText },
  } = controllerMethods;

  const [selectedMode, setSelectedMode] = useState<'richText' | 'image' | null>(null);

  const [{ hasImage }, setRichTextContentState] = useState({
    hasImage: false,
    hasFormula: false,
  });

  const handleContentStateChange = ({ hasImage, hasFormula }: { hasImage: boolean; hasFormula: boolean }) => {
    setRichTextContentState({ hasImage, hasFormula });
  };

  const clearRichTextEditor = () => {
    onChangeRichText('');
    setInitialRichText('' as PathValue<T, Path<T>>);
    setRichTextContentState({
      hasImage: false,
      hasFormula: false,
    });
    editorRef.current?.clear();

    // focus rich-text editor
    editorRef.current?.restoreSelection(null);
  };

  const handleRichTextButtonClick = () => {
    if (hasImage) {
      clearRichTextEditor();
      return;
    }
    editorRef.current?.restoreSelection();
    setSelectedMode('richText');
  };

  const handleBeforeImageUpload = () => {
    clearRichTextEditor();
  };

  useEffect(
    function onRichTextContentTypeChange() {
      const isMounted = getIsMounted();
      if (!isMounted) return;

      if (hasImage) setSelectedMode('image');
      else setSelectedMode('richText');
    },
    [getIsMounted, hasImage]
  );

  useEffect(
    function onSelectImageMode() {
      if (!editorRef.current) return;
      if (!hasImage) return;

      Transforms.removeNodes(editorRef.current, {
        at: [],
        match: (node, path) => Element.isElement(node) && node.type !== 'image',
      });
    },
    [hasImage]
  );

  return {
    editorRef,
    initialRichText,
    richText,
    handleContentStateChange,
    handleRichTextButtonClick,
    handleBeforeImageUpload,
    handleClearButtonClick: clearRichTextEditor,
    selectedMode,
  };
};

export default useQuestionOption;
